/**
 * Development scenario switch.
 *
 * `FND-11` requires every screen to *declare* all five interaction states. A
 * declaration nobody can see is a comment. This makes each state reachable on
 * demand, so "the empty state is designed" is something you look at rather than
 * something you believe.
 *
 * It only affects the in-memory fake. When the generated client replaces the
 * fake, this control disappears with it — which is why it lives in `lib/api`
 * and not in the UI store.
 */

import { ref, type Ref } from 'vue'

export const SCENARIOS = ['happy', 'empty', 'error', 'slow', 'partial'] as const
export type Scenario = (typeof SCENARIOS)[number]

export const SCENARIO_LABELS: Record<Scenario, string> = {
  happy: 'Populated',
  empty: 'Empty',
  error: 'Server error',
  slow: 'Loading (5s)',
  partial: 'Partial / stale data'
}

const STORAGE_KEY = 'match-point.dev-scenario'

function initial (): Scenario {
  if (typeof window === 'undefined') return 'happy'
  const fromQuery = new URLSearchParams(window.location.search).get('scenario')
  const fromStorage = window.sessionStorage?.getItem(STORAGE_KEY)
  const candidate = fromQuery ?? fromStorage
  return (SCENARIOS as readonly string[]).includes(candidate ?? '') ? (candidate as Scenario) : 'happy'
}

export const scenario: Ref<Scenario> = ref(initial())

export function setScenario (next: Scenario): void {
  scenario.value = next
  if (typeof window !== 'undefined') window.sessionStorage?.setItem(STORAGE_KEY, next)
}

/** How long the fake pretends the network took. */
export function latencyMs (): number {
  return scenario.value === 'slow' ? 5000 : 220
}

export const isEmptyScenario = (): boolean => scenario.value === 'empty'
export const isErrorScenario = (): boolean => scenario.value === 'error'
export const isPartialScenario = (): boolean => scenario.value === 'partial'
